/**
 * Der Allergenfilter für Gäste (S63, docs/26-allergenfilter.md): the switch under „Im Netz“ (Bild 5)
 * and, with it on, how many dishes of a card lack their allergens, before publishing too (A7, Bild 6).
 */
import { useState } from "react";
import { keep } from "../kept.ts";
import { api, dishesById, messageOf, store } from "../store.ts";
import type { Data } from "../store.ts";

/** A menu as far as its dishes go: saved or a draft. */
type Dishes = { sections: { items: { dish: string }[] }[] } | null | undefined;

/** The dishes of these menus whose allergens are not kept up (S5: null), each once. */
export function unknownOn(data: Data, menus: readonly Dishes[]): number {
  const dishes = dishesById(data);
  const ids = new Set(menus.flatMap((m) => (m ? m.sections.flatMap((s) => s.items.map((i) => i.dish)) : [])));
  return [...ids].filter((id) => dishes.get(id)?.allergens === null).length;
}

/** „Jetzt nachtragen“: the dishes and drinks under „Kataloge“ with the filter „ohne Allergene“. */
function openMissing() {
  for (const p of ["dishes", "drinks"]) {
    keep(`${p}:missing`, true);
    keep(`${p}:q`, "");
    keep(`${p}:cat`, null);
  }
  location.hash = "#/kataloge";
}

/** How many dishes lack their allergens, with „Jetzt nachtragen“; with the filter on only (A7). */
export function FilterMissing({ data, menus, which }: { data: Data; menus: readonly Dishes[]; which: "der gezeigten Karte" | "dieser Karte" }) {
  // Nothing shown yet (no card on the net): nothing to count, and nothing to praise either.
  if (data.settings.allergenFilter !== true || !menus.some(Boolean)) return null;
  const n = unknownOn(data, menus);
  if (n === 0) return which === "der gezeigten Karte" ? <p className="ok-text" data-testid="filter-missing-none">Bei allen Speisen {which} sind die Allergene gepflegt.</p> : null;
  return (
    <div className="warn-box" data-testid="filter-missing-count">
      {n === 1 ? `Bei 1 Speise ${which} fehlen die Allergene.` : `Bei ${n} Speisen ${which} fehlen die Allergene.`} Gäste, die filtern, sehen sie mit „Allergene nicht angegeben“.{" "}
      <button className="link inline" onClick={openMissing} data-testid="filter-missing-open">
        Jetzt nachtragen ›
      </button>
    </div>
  );
}

/** The switch (A1): off by default, only owner and manager; it holds on the net at once. */
export function AllergenFilterSwitch({ data, shown }: { data: Data; shown: readonly Dishes[] }) {
  const [asked, setAsked] = useState<boolean | null>(null);
  const [error, setError] = useState("");
  const on = asked ?? data.settings.allergenFilter === true;
  async function toggle(checked: boolean) {
    setAsked(checked);
    setError("");
    try {
      await store.change(() => api("/api/settings", { method: "PUT", body: { allergenFilter: checked } }));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setAsked(null);
    }
  }
  return (
    <div className="allergen-filter" data-testid="allergen-filter">
      <label className="check">
        <input type="checkbox" checked={on} onChange={(e) => void toggle(e.target.checked)} data-testid="allergen-filter-switch" />
        <span>
          <b>Allergenfilter für Gäste</b>
          <span className="hint" style={{ display: "block", margin: 0 }}>
            Gäste können Speisen mit bestimmten Allergenen ausblenden. Speisen ohne gepflegte Allergene bleiben sichtbar, aber mit dem Hinweis „Allergene nicht angegeben“.
          </span>
        </span>
      </label>
      {on && <FilterMissing data={data} menus={shown} which="der gezeigten Karte" />}
      {error && <p className="error-box">{error}</p>}
    </div>
  );
}
